import React from "react";
import { Dropdown, Popconfirm, Switch, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";

import CustomPagination from "../../../../core/common/customPagination/CustomPagination";

import type {
  EmailTemplate,
  EmailTemplateCategory,
} from "../../../../core/data/redux/email-templates/types";


interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface TemplateStatsByTemplateId {
  [templateId: number]: {
    delivered: number;
    failed: number;
    total: number;
  };
}

interface Props {
  templates: EmailTemplate[];
  categories: EmailTemplateCategory[];
  loading: boolean;
  pagination: Pagination;
  stats?: TemplateStatsByTemplateId;
  statsDays?: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onEdit: (tpl: EmailTemplate) => void;
  onDuplicate: (tpl: EmailTemplate) => void;
  onDelete: (tpl: EmailTemplate) => void;
  onToggleActive: (tpl: EmailTemplate) => void;
  onTestSend: (tpl: EmailTemplate) => void;
}

const EmailTemplatesTable: React.FC<Props> = ({
  templates,
  categories,
  loading,
  pagination,
  stats,
  statsDays = 30,
  onPageChange,
  onLimitChange,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleActive,
  onTestSend,
}) => {
  const findCategoryConfig = (code?: string | null) =>
    code ? categories.find((c) => c.value === code) : undefined;

  const getActionItems = (tpl: EmailTemplate): MenuProps["items"] => [
    {
      key: "edit",
      icon: <i className="ti ti-edit" style={{ color: "#f59e0b" }} />,
      label: "Edit template",
      onClick: () => onEdit(tpl),
    },
    {
      key: "duplicate",
      icon: <i className="ti ti-copy" style={{ color: "#3b82f6" }} />,
      label: "Duplicate",
      onClick: () => onDuplicate(tpl),
    },
    {
      key: "test-send",
      icon: <i className="ti ti-send" style={{ color: "#10b981" }} />,
      label: "Send test email",
      onClick: () => onTestSend(tpl),
    },
    { type: "divider" },
    {
      key: "delete",
      icon: <i className="ti ti-trash" />,
      danger: true,
      label: (
        <Popconfirm
          title="Delete this template?"
          description="This is a soft-delete; recoverable on the backend."
          onConfirm={() => onDelete(tpl)}
          okText="Delete"
          cancelText="Cancel"
          placement="left"
        >
          <span>Delete</span>
        </Popconfirm>
      ),
    },
  ];

  const columns: ColumnsType<EmailTemplate> = [
    {
      title: "Sr No.",
      key: "srNo",
      width: 70,
      align: "center",
      fixed: "left",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
          {((pagination.current_page || 1) - 1) * (pagination.per_page || 10) +
            index +
            1}
        </span>
      ),
    },
    {
      title: <Tooltip title="Template name & subject">Template</Tooltip>,
      dataIndex: "template_name",
      key: "template_name",
      fixed: "left",
      width: 340,
      render: (text, record) => {
        const cat = findCategoryConfig(record.category);
        const tint = cat?.color || "#1677ff";
        return (
          <Tooltip
            title={
              <div style={{ padding: 4 }}>
                <div className="fw-bold pb-1 mb-1 border-bottom border-secondary">
                  {text}
                </div>
                <div style={{ fontSize: 12 }}>
                  <i className="ti ti-hash me-1" />
                  {record.template_code}
                </div>
                <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>
                  Subject: {record.subject}
                </div>
              </div>
            }
            color="#1e293b"
            placement="right"
          >
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 9,
                  background: `${tint}1a`,
                  color: tint,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: 16,
                }}
              >
                {cat?.icon || <i className="ti ti-mail" />}
              </div>
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <h6
                  className="fw-medium mb-1 text-truncate"
                  style={{ maxWidth: 260, fontSize: 13.5, color: "#0f172a" }}
                >
                  {text}
                </h6>
                <span
                  className="text-muted text-truncate"
                  style={{ fontSize: 12, maxWidth: 260 }}
                >
                  {record.subject}
                </span>
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="Stable identifier used in code">Template Code</Tooltip>,
      dataIndex: "template_code",
      key: "template_code",
      width: 220,
      render: (code) => (
        <Tag
          style={{
            fontFamily: "monospace",
            fontSize: 11,
            background: "#eff6ff",
            color: "#1e40af",
            border: "1px solid #dbeafe",
          }}
        >
          {code}
        </Tag>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 160,
      render: (category) => {
        const cat = findCategoryConfig(category);
        if (!cat) {
          return <span className="text-muted">—</span>;
        }
        return (
          <Tag
            style={{
              fontSize: 11,
              background: `${cat.color || "#1677ff"}14`,
              color: cat.color || "#1677ff",
              border: `1px solid ${cat.color || "#1677ff"}33`,
            }}
          >
            {cat.icon} {cat.label}
          </Tag>
        );
      },
    },
    {
      title: (
        <Tooltip title={`Sends in the last ${statsDays} days`}>
          Sends ({statsDays}d)
        </Tooltip>
      ),
      key: "stats",
      width: 150,
      align: "center",
      render: (_, record) => {
        const s = stats?.[record.id];
        if (!s || s.total === 0) {
          return <span className="text-muted" style={{ fontSize: 11 }}>—</span>;
        }
        const failedPct = s.total > 0 ? Math.round((s.failed / s.total) * 100) : 0;
        return (
          <Tooltip
            title={
              <div style={{ fontSize: 11 }}>
                <div><strong>Total:</strong> {s.total}</div>
                <div><strong>Delivered:</strong> {s.delivered}</div>
                <div><strong>Failed:</strong> {s.failed}</div>
              </div>
            }
          >
            <div className="d-flex align-items-center justify-content-center" style={{ gap: 6 }}>
              <Tag
                style={{
                  fontSize: 11,
                  margin: 0,
                  background: "#eff6ff",
                  color: "#1e40af",
                  border: "none",
                }}
              >
                <i className="ti ti-send me-1" />
                {s.total}
              </Tag>
              {s.failed > 0 ? (
                <Tag
                  style={{
                    fontSize: 10,
                    margin: 0,
                    background: "#fee2e2",
                    color: "#991b1b",
                    border: "none",
                  }}
                >
                  {failedPct}% fail
                </Tag>
              ) : null}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Last Modified",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 180,
      render: (val) =>
        val ? (
          <span style={{ fontSize: 12, color: "#475569" }}>
            {new Date(val).toLocaleString()}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      align: "center",
      render: (active, record) => (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ gap: 6 }}
        >
          <Switch
            size="small"
            checked={Boolean(active)}
            onChange={() => onToggleActive(record)}
          />
          <Tag
            style={{
              fontSize: 10,
              margin: 0,
              background: active ? "#dcfce7" : "#f3f4f6",
              color: active ? "#166534" : "#6b7280",
              border: "none",
            }}
          >
            {active ? "Active" : "Inactive"}
          </Tag>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <button
            type="button"
            className="btn btn-icon btn-sm btn-light"
            style={{ borderRadius: 6 }}
            onClick={(e) => e.stopPropagation()}
          >
            <i className="ti ti-dots-vertical" />
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="card-body p-0 mb-3 border-0">
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{
            minHeight: 400,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
          }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            />
            <p className="mt-3 text-muted">Loading templates...</p>
          </div>
        </div>
      ) : templates.length > 0 ? (
        <div
          className="bg-white"
          style={{
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
          }}
        >
          <Table
            className="custom-data-table"
            columns={columns}
            dataSource={templates}
            rowKey="id"
            size="middle"
            pagination={false}
            scroll={{ x: 1100 }}
            rowClassName={(_, index) =>
              index % 2 === 0
                ? "data-table-row-light"
                : "data-table-row-dark"
            }
          />
          <CustomPagination
            currentPage={pagination.current_page || 1}
            totalPages={pagination.total_pages || 1}
            total={pagination.total}
            limit={pagination.per_page || 10}
            hasNextPage={
              (pagination.current_page || 1) < (pagination.total_pages || 1)
            }
            hasPrevPage={(pagination.current_page || 1) > 1}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{
            minHeight: 400,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
          }}
        >
          <div className="text-center" style={{ padding: 40 }}>
            <i
              className="ti ti-mail-off"
              style={{ fontSize: 48, color: "#cbd5e1" }}
            />
            <h5 className="mt-3 mb-2 fw-semibold">No templates found</h5>
            <p className="text-muted mb-0">
              Try adjusting your filters, or click{" "}
              <strong>New Template</strong> to create one.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesTable;
